import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { cn } from '@/lib/cn';
import { resolveLegalDocumentLocale, type LegalDocumentLocale } from './legalContent';

type LegalTab = 'terms' | 'privacy';

interface LegalTabsProps {
  activeTab: LegalTab;
}

const LEGAL_TABS_BY_LOCALE: Record<LegalDocumentLocale, Array<{ id: LegalTab; label: string; to: string }>> = {
  ko: [
    { id: 'terms', label: '이용약관', to: ROUTES.COMMON.TERMS },
    { id: 'privacy', label: '개인정보처리방침', to: ROUTES.COMMON.PRIVACY },
  ],
  en: [
    { id: 'terms', label: 'Terms of Service', to: ROUTES.COMMON.TERMS },
    { id: 'privacy', label: 'Privacy Policy', to: ROUTES.COMMON.PRIVACY },
  ],
  ar: [
    { id: 'terms', label: 'شروط الخدمة', to: ROUTES.COMMON.TERMS },
    { id: 'privacy', label: 'سياسة الخصوصية', to: ROUTES.COMMON.PRIVACY },
  ],
  id: [
    { id: 'terms', label: 'Ketentuan Layanan', to: ROUTES.COMMON.TERMS },
    { id: 'privacy', label: 'Kebijakan Privasi', to: ROUTES.COMMON.PRIVACY },
  ],
  mn: [
    { id: 'terms', label: 'Үйлчилгээний нөхцөл', to: ROUTES.COMMON.TERMS },
    { id: 'privacy', label: 'Нууцлалын бодлого', to: ROUTES.COMMON.PRIVACY },
  ],
};

const LEGAL_NAV_ARIA_BY_LOCALE: Record<LegalDocumentLocale, string> = {
  ko: '법적 고지 메뉴',
  en: 'Legal notice menu',
  ar: 'قائمة الإشعارات القانونية',
  id: 'Menu pemberitahuan hukum',
  mn: 'Хууль эрх зүйн мэдэгдлийн цэс',
};

export default function LegalTabs({ activeTab }: LegalTabsProps): React.ReactElement {
  const { i18n } = useTranslation();
  const legalLocale = resolveLegalDocumentLocale(i18n.language);
  const tabs = LEGAL_TABS_BY_LOCALE[legalLocale];

  return (
    <nav className="pt-10" aria-label={LEGAL_NAV_ARIA_BY_LOCALE[legalLocale]}>
      <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;

          return (
            <Link
              key={tab.id}
              to={tab.to}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'rounded-md px-4 py-2 text-sm font-semibold transition-colors',
                isActive
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:bg-white hover:text-gray-900'
              )}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
